import { useState, useRef, useEffect } from "react";
import Icon from "../Icon";

const VideoThumbnail = ({
  src,
  alt,
  width,
  height,
  playButtonSize,
}: {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  playButtonSize?: string;
}) => {
  const [thumbnail, setThumbnail] = useState("");
  const [duration, setDuration] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const captureThumbnail = () => {
      const video = videoRef.current;
      if (!video) return;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = width ?? video.videoWidth;
      canvas.height = height ?? video.videoHeight;

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      setThumbnail(canvas.toDataURL("image/jpeg"));
    };

    const handleLoadedMetadata = (e: Event) => {
      const video = e.target as HTMLVideoElement;
      video.currentTime = 0.1;

      // Format the duration
      if (video.duration) {
        const minutes = Math.floor(video.duration / 60);
        const seconds = Math.floor(video.duration % 60);
        setDuration(`${minutes}:${seconds.toString().padStart(2, "0")}`);
      }
    };

    if (videoRef.current) {
      videoRef.current.addEventListener("loadeddata", captureThumbnail);
      videoRef.current.addEventListener("loadedmetadata", handleLoadedMetadata);
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener("loadeddata", captureThumbnail);
        videoRef.current.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
      }
    };
  }, [src]);

  return (
    <div className="relative w-full h-auto cursor-pointer">
      {/* Video Element */}
      <video
        ref={videoRef}
        src={src}
        aria-label={alt}
        playsInline
        muted
        poster={thumbnail}
        className="w-full h-auto rounded-lg object-cover"
      />

      {/* Play Icon Overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon
          className="p-2 bg-white text-cyan-600 rounded-full shrink-0 hover:scale-105 transition-transform mb-5"
          name="play"
          size={playButtonSize ?? "70px"}
        />
      </div>

      {/* Duration Overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/70 to-transparent pointer-events-none rounded-b-lg">
        {/* Duration Text */}
        {duration && (
          <div className="absolute bottom-2 right-2 text-white text-sm font-medium">
            {duration}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoThumbnail;
